import { InjectionToken } from '@angular/core';
import { Injectable, Inject } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from "@angular/router";

import { Subject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { AppConstants} from '@unpispas/upp-defs';
import { HttpClient } from '@angular/common/http';

/**
 * @const {InjectionToken<string>} langsPath
 * @description Injection token representing the base path where language JSON files are stored.
 */
export const langsPath = new InjectionToken<string>('langsPath');

/** @internal */ type __tstring = string | null;
/** @internal */ type __targs = Array <string | number | null | undefined>;
/** @internal */ type __trinfo = { tr: string, args: __targs };

/** @internal */
class __trString{
    private _id: __tstring = null;
    private _args: __targs = [];
    private _tr: __tstring = null;
    
    constructor(private lang: _translator, id: __tstring, args: __targs){
        this._id = id;
        this._args = args;
    }

    get value() {
        if (!this._tr){
            this._tr = String(this.lang._translate(this._id, this._args));
        }
        return this._tr;
    }    
}

/** @internal */
class __erString{
    private _str = "";
    
    constructor(str: string){
        this._str = str;    
    }
    
    get value() {
        return this._str;
    }
}

/**
 * @internal
 * @class _translator
 * @description Internal class to handle translation management, including loading modules, caching, and error handling.
 */
class _translator {    
    private strings = new Map <string, __trinfo> ();
    private current: string | null = null;
    private modules = new Set <string> ();
    private trcache = new Map <string, __trString> ();
    private loading = 0;

    /**
     * @getter _strings
     * @description Returns the map of all translation strings currently loaded.
     * @returns {Map<string, __trinfo>} The map of translation strings.
     */    
    get _strings(){
        return this.strings;
    }
    
    /**
     * @private
     * @method _trerror
     * @description Handles translation errors by generating a fallback string and optionally logging the error to the console.
     * @param {string} id - The ID of the translation string.
     * @param {__targs} args - The arguments provided for the translation placeholders.
     * @param {__tstring} errstr - Optional error message to log to the console.
     * @returns {string} The generated fallback string.
     */    
    private _trerror(id: string,  args: __targs, errstr: __tstring) {
        if(errstr) {
            console.error(errstr);
        }

        let tr = id + " [ ";
        for(let i=0; i < args.length; i++) {
            tr += args[i] + ", ";
        }
        if (args.length > 0) {
            tr = tr.slice(0, -2);
        }
        
        tr += " ]";
        return tr;
    }
    
    /**
     * @constructor
     * @param {HttpClient} http - Angular HttpClient for API requests.
     * @param {string} langspath - Base path for loading language JSON files.
     */    
    constructor(private http: HttpClient, private langspath: string) { 
        // nothing to do (initialized when language is set)
    }
    
    /**
     * @public
     * @method SetLanguage
     * @description Sets the current language and loads the required translation modules.
     * @param {string} language - The target language code.
     * @param {Set<string>} modules - Set of module names to load.
     * @returns {Promise<boolean>} Whether the language was successfully set.
     */    
    public async SetLanguage(language: string, modules: Set <string>) : Promise <boolean> {
        if (language != this.current){
            this.current = language;

            const _promises =  [];
            for(const module of modules){
                _promises.push(this.LoadModule(module));
            }

            const results = await Promise.all(_promises);
            return results.every(result => result === true);
        }

        return true;
    }

    /**
     * @public
     * @method LoadModule
     * @description Loads translation strings for a specific module.
     * @param {string} name - The name of the module.
     * @returns {Promise<boolean>} Whether the module was successfully loaded.
     */    
    public LoadModule(name: string) : Promise <boolean> {
        if (this.current){
            return this._load_module(name, this.current, false);
        }

        console.error("LANGUAGE] Could not load module before setting the language");
        return Promise.resolve(false);;
    }

    /**
     * @public
     * @method _translate
     * @description Resolves a translation string by ID and replaces placeholders with provided arguments.
     * @param {__tstring} id - The ID of the translation string.
     * @param {__targs} args - Arguments to replace placeholders in the string.
     * @returns {string | null} The resolved translation string, or null if not found.
     */    
    public _translate(id: __tstring, args: __targs): string | null {
        if (id == null){
            return null;
        }

        const trinfo = this.strings.get(id) || null;
        if (trinfo){
            if (!trinfo.args) trinfo.args = [];     // add empty arguments list as default
            if (trinfo.args.length != args.length) {
                return this._trerror(id, args, "ERROR: invalid number of arguments for translation [" + id + "]");
            }
            
            let tr = trinfo.tr;
            for (let i=0; i < trinfo.args.length; i++) {
                const re = new RegExp("{{" + trinfo.args[i] + "}}", "g");
                tr = tr.replace(re, String(args[i]));
            }
            return tr;
        }

        return null;
    }

    /**
     * @public
     * @method tr
     * @description Translates a string ID with optional arguments. Uses a cache for optimization.
     * @param {string} id - The ID of the string to translate.
     * @param {__targs} args - Optional arguments to replace placeholders in the translation string.
     * @returns {string} The translated string, or an error placeholder if the ID is not found.
     */    
    public tr(id: string, args: __targs = []): string {
        let _str: __trString | __erString | null = null;

        const _key = JSON.stringify({ id: id, args: args });
        _str = this.trcache.get(_key) || null;
        if (!_str){
            _str = new __erString((this.loading > 0) ? '...' : this._trerror(id, args, null));
            if (this.strings.has(id)){
                this.trcache.set(_key, _str = new __trString(this, id, args));
            }
        }
        return _str.value;
    }

    /**
     * @private
     * @method _load_json
     * @description Loads translation strings from a JSON object into the internal string map.
     * @param {any} json - The JSON object containing translation strings.
     * @param {boolean} replace - Whether to replace existing strings with new ones.
     */    
    private _load_json(json: any, replace: boolean) {
        for(let i=0; i < json.length; i++){
            const entry = json[i];
            if (!replace && this.strings.has(entry.id)){
                console.warn("WARNING: existing string '" + entry.id + "'");
            }
            this.strings.set(entry.id, { args: entry.args, tr: entry.tr });
        }

        // Clear cached translations (including temporary "...") after loading.
        this.trcache.clear();
    }
    
    /**
     * @private
     * @method _load_module
     * @description Loads translation strings for a specific module and language.
     * @param {string} name - The module name.
     * @param {string} language - The language code.
     * @param {boolean} replace - Whether to replace existing strings.
     * @returns {Promise<boolean>} Resolves to true if the module is successfully loaded; otherwise, false.
     */    
    private _load_module(name: string, language: string, replace: boolean): Promise <boolean> {
        return new Promise <boolean> ((resolve) => {
            if (!this.modules.has(name)){
                this.loading++;    

                const _request = this.http.get(this.langspath + '/' + name + '/' + language.toLowerCase() + '.json');
                _request.pipe(take(1)).subscribe({
                    next: (data) => {
                        this._load_json(data, replace);
                        console.info("[LANGUAGE] Module '" + name + "' strings loaded for language '" + language + "'");
                        this.modules.add(name);
                        resolve(true);    
                    },
                    error: (error) => {
                        console.error("LANGUAGE] Could not load strings for module '" + name + "'.");
                        console.error(error);
                        this.loading--;
                        resolve(false);    
                    },
                    complete: () => {
                        this.loading--; 
                    }
                });          
            }
            else {
                resolve(true);  // already loaded (considered successful)
            }
        });
    }    
}

/**
 * @class languageService
 * @description A service to manage language translations and language-related settings for the application.
 * The service loads translation strings for various modules and supports switching between different languages.
 * It also provides translation functionality with caching mechanisms for optimized performance.
 */
@Injectable({
    providedIn: 'root'
})
export class languageService {
    /** Supported language code → flag icon code (single source for {@link GetLanguages} and {@link FlagFor}). */
    private static readonly _languages: Record<string, string> = {
        ES: 'ES',
        EU: 'ES-PV',
        EN: 'GB',
        ZH: 'CN',
    };

    private _lngsets: { [key: string]: _translator } = {}

    private _current: string | null = null; 
    private modules = new Set <string> ();

    /**
     * @getter _strings
     * @description Retrieves the map of translation strings for the current language.
     * @returns {Map<string, __trinfo>} The map of loaded translation strings.
     */    
    get _strings(){
        if (this._current){
            return this._lngsets[this._current]._strings;
        }
        return new Map <string, __trinfo> ();
    }    

   /**
     * @constructor
     * @param {HttpClient} http - Angular HttpClient for fetching translation files.
     * @param {string} langspath - The base path for translation JSON files.
     * @param {ActivatedRoute} route - Angular ActivatedRoute for accessing URL parameters.
     */    
    constructor(private http: HttpClient, @Inject(langsPath) private langspath: string, private route: ActivatedRoute) { 

        // first option - user browser language
        let _userLang = navigator.language || AppConstants.defaultLanguage.toUpperCase(); 
        if (_userLang){
            _userLang = _userLang.substring(0, 2).toUpperCase();
            
            console.info("[BROWSER LANGUAGE]: " + _userLang);
            let _supported = this.GetLanguages().includes(_userLang) ? _userLang : null;

            _supported = _supported || AppConstants.defaultLanguage.toUpperCase();
            if (_supported){
                this.AddLanguage(_supported).then(
                () => {
                    this.SetLanguage(_supported, true);
                });
            }
        }

        // second option - user URL language
        this.route.queryParams.subscribe(params => {
            const _urlLang = params['lang'];

            if (_urlLang) {  // use url language
                console.info("[URL GET LANGUAGE]: " + _urlLang);
                const _targetLang = String(_urlLang).toUpperCase();

                const _supported = this.GetLanguages().includes(_targetLang) ? _targetLang : null;

                if (_supported) {
                    this.AddLanguage(_supported).then(() => {
                        this.SetLanguage(_supported, true);
                    });       
                }
            }
        });

        this.LoadModule('base');
    }    

    /**
     * @public
     * @method StrInfo
     * @description Retrieves detailed translation information for a given string ID in the current language.
     * @param {string} id - The ID of the translation string.
     * @returns {__trinfo | null} Translation information, or null if the string ID is not found.
     */    
    public StrInfo(id: string){
        if (!this._current){
            return null;
        }

        const _lang = this._lngsets[this._current];
        if (!_lang){
            console.error("[TRANSLATE]: No current language is set")
            return null;
        }

        return _lang._strings.get(id);
    }

    /**
     * Supported language codes in catalog application order (fallback chain).
     *
     * Returns `ES`, `EU`, `EN`, `ZH`. Context-specific preference (UI language, place default, …)
     * is resolved by consumers; this method only exposes the catalog order.
     */
    public GetLanguages(): string[] {
        return Object.keys(languageService._languages);
    }

    /**
     * @public
     * @method FlagFor
     * @description Flag icon code for a language (used with `fi fi-<code>` CSS classes).
     * @param {string} language - Language code.
     * @returns {string | null} Flag code (for example `GB` for `EN`), or `null` when the code is not supported.
     */    
    public FlagFor(language: string): string | null {
        return languageService._languages[language] ?? null;
    }

    private _onSetLanguage = new Subject <void> ();

    /**
     * @public
     * @property OnSetLanguage
     * @description Observable that emits notifications whenever the active language changes.
     */    
    public OnSetLanguage = this._onSetLanguage.asObservable();

    /**
     * @public
     * @method LoadModule
     * @description Loads translation strings for a specified module in all available languages.
     * @param {string} name - The name of the module to load.
     * @returns {Promise<boolean>} Resolves to true if the module was successfully loaded; otherwise, false.
     */    
    public async LoadModule(name: string): Promise <boolean> {
        this.modules.add(name);

        const _promises: Array<Promise<boolean>> = [];
        const _currentLanguage = this._current;

        for (const _language in this._lngsets){
            const _loadPromise = this._lngsets[_language].LoadModule(name);
            _promises.push(_loadPromise);

            if ((_currentLanguage) && (_language === _currentLanguage)){
                void _loadPromise.finally(
                () => {
                    this._onSetLanguage.next();
                });
            }
        }

        const _loaded = await Promise.all(_promises);

        const _allok = _loaded.every(
        (result) => {
            return result === true
        });

        this._onSetLanguage.next();

        return _allok;
    }

    /**
     * Ensures the active language and requested module are loaded before continuing.
     * This is useful for one-shot UI flows (modals/alerts) that should not render
     * placeholders while language resources are still being initialized.
     *
     * @param module - Module name that must be ready for the current language.
     * @returns Resolves to true when language and module are ready.
     */
    public async WaitReady(module = 'base'): Promise<boolean> {
        const _module = String(module || '').trim();
        if (!_module){
            return false;
        }

        this.modules.add(_module);

        const _language = this.GetLanguage(true);
        if (!(await this.AddLanguage(_language))){
            return false;
        }

        if (!(await this.SetLanguage(_language, true))){
            return false;
        }

        const _translator = this._lngsets[_language];
        if (!_translator){
            return false;
        }

        return await _translator.LoadModule(_module);
    }

    /**
     * @private
     * @method AddLanguage
     * @description Adds a new language to the service and loads its translation strings.
     * @param {string} language - The language code to add.
     * @returns {Promise<boolean>} Resolves to true if the language was successfully added; otherwise, false.
     */    
    private async AddLanguage(language: string): Promise <boolean> {
        const _language = String(language || '').toUpperCase();
        if (!_language){
            return false;
        }

        if (_language in this._lngsets){
            return true;     // already included
        }

        this._lngsets[_language] = new _translator(this.http, this.langspath);
        if (this._lngsets[_language]){
            return await this._lngsets[_language].SetLanguage(_language, this.modules);
        }

        return false;
    }

    /**
     * @private
     * @method AddLanguage
     * @description Adds a new language to the service and loads its translation strings.
     * @param {string} language - The language code to add.
     * @returns {Promise<boolean>} Resolves to true if the language was successfully added; otherwise, false.
     */    
    public async SetLanguage(language: string, global = true): Promise <boolean>  {
        const _language = String(language || '').toUpperCase();
        if (!_language){
            return false;
        }

        if (this._current == _language){
            return true;
        }

        const _lang = this._lngsets[_language];
        if (!_lang){
            const success = await this.AddLanguage(_language);
            if (!success){
                return false;
            }
        }

        if (global){
            this._current = _language;
            this._onSetLanguage.next();
        }

        return true;
    }

    /**
     * @public
     * @method GetLanguage
     * @description Retrieves the current active language.
     * @param {boolean} [toUpper=false] - Whether to return the language code in uppercase.
     * @returns {string} The active language code, or the default language if no language is set.
     */    
    public GetLanguage(toUpper = false): string {
        if (this._current){
            return (toUpper)? this._current.toUpperCase() : this._current;
        }

        return AppConstants.defaultLanguage.toUpperCase();
    }
    
    /**
     * @public
     * @method tr
     * @description Translates a string ID into the corresponding translation using the current or specified language.
     * @param {string} id - The ID of the translation string.
     * @param {__targs} [args=[]] - Optional arguments to replace placeholders in the translation.
     * @param {string | null} [language=null] - Optional target language for the translation.
     * @returns {string} The translated string, or "..." if the language is still loading.
     */    
    public tr(id: string, args: __targs = [], language: string | null = null): string {
        const _target = language || this._current;
        if (_target){
            const _lang = this._lngsets[_target];
            if (!_lang){
                return "...";   // strings are being loaded
            }    

            return _lang.tr(id, args);
        }

        return "...";    // no language provided
    }
}

/**
 * @class languageSupport
 * @description A utility class that facilitates the integration of language translation with Angular components.
 * This class observes language changes and provides methods for translating strings with caching and argument support.
 */
export class languageSupport {
    public language: string | null = null;
    private _islocal = false;
    
    private _strcache = new Map <string, string> ();
    private _argcache = new Map <string, __trinfo>();

    public _lang: languageService;
    public _chng: ChangeDetectorRef | null = null;

    private _language_subscription: Subscription | null = null;

    /**
     * @constructor
     * @description Initializes the language support instance, subscribes to language change notifications,
     * and binds the provided `languageService` and `ChangeDetectorRef`.
     * @param {languageService} lang - The language service to use for translations.
     * @param {ChangeDetectorRef | null} chng - The Angular change detector for triggering view updates.
     */    
    constructor(lang: languageService, chng: ChangeDetectorRef | null){
        this._lang = lang;
        this._chng = chng;

        this._language_subscription = this._lang.OnSetLanguage.subscribe(
        () => {
            if (!this._islocal){
                this.language = this._lang.GetLanguage();
            }

            this._strcache.clear();
            this._argcache.clear();

            if (this._chng){
                this._chng.markForCheck();
            }
        });

        this.language = this._lang.GetLanguage();
    }
    
    /**
     * @public
     * @method OnDestroy
     * @description Cleans up the subscription to the language change observable when the component is destroyed.
     * @returns {void}
     */    
    OnDestroy(){
        if (this._language_subscription){
            this._language_subscription.unsubscribe();
        }
        this._language_subscription = null;
    }

    /**
     * @private
     * @method _translate
     * @description Resolves a translation string by replacing placeholders with provided arguments.
     * @param {__trinfo} trinfo - Translation information containing placeholders and the base string.
     * @param {__targs} args - Arguments to replace the placeholders in the translation string.
     * @returns {string | null} The resolved translation string, or null if arguments do not match placeholders.
     */
    private _translate(trinfo: __trinfo, args: __targs) {
        if (!trinfo.args) { // add empty arguments list as default
            trinfo.args = [];     
        }

        if (trinfo.args.length != args.length) {
            return null;
        }
        
        let tr = trinfo.tr;
        for (let i=0; i < trinfo.args.length; i++) {
            const re = new RegExp("{{" + trinfo.args[i] + "}}", "g");
            tr = tr.replace(re, String(args[i]));
        }
        
        return tr;
    }

    /**
     * @public
     * @method SetLanguage
     * @description Sets the current language and updates translations for the component. 
     * Clears the cache and triggers a view update if a new language is set.
     * @param {string} language - The language code to set as current.
     * @returns {Promise<void>} Resolves once the language is set and view updated.
     */    
    public async SetLanguage(language: string){
        const _language = String(language || '').toUpperCase();
        if (!_language){
            return;
        }

        if ((_language != this.language) && await this._lang.SetLanguage(_language, false)){
            this._islocal = true;
            this.language = _language;
            this._strcache.clear();

            if (this._chng){
                this._chng.markForCheck();    
            }
        }
    }

    /**
     * @public
     * @method tr
     * @description Translates a string ID to its corresponding translated value, using the provided arguments (if any).
     * If no arguments are provided, the translation is cached for optimized future access.
     * @param {string} id - The ID of the translation string.
     * @param {Array<any>} [args=[]] - Optional arguments to replace placeholders in the translation.
     * @returns {string} The translated string.
     */        
    public tr(id: string, args: __targs = []): string {
        if (args.length == 0){
            let _str = this._strcache.get(id) || null;
            if (_str){
                return _str;
            }

            _str = this._lang.tr(id, args, this.language);
            if (_str && (_str != '...') && (_str[0] != '@') && (args.length == 0)) {   // do not cache pending/loading placeholder
                this._strcache.set(id, _str); 
            }
            return _str;
        }
        else {
            let _trinfo: __trinfo | undefined | null = null; 

            _trinfo = this._argcache.get(id);
            if (_trinfo){
                const _str = this._translate(_trinfo, args);
                if (_str){
                    return _str;
                }        
            }

            _trinfo = this._lang.StrInfo(id);
            if (_trinfo){
                this._argcache.set(id, _trinfo);
                const _str = this._translate(_trinfo, args);
                if (_str){
                    return _str;
                }
            }

            return this._lang.tr(id, args, this.language);
        }
    }
}


